import { Injectable } from '@angular/core';
import { environment } from '@env/environment';

export enum LogLevel {
  ERROR = 0,
  WARN = 1,
  INFO = 2,
  DEBUG = 3,
  TRACE = 4
}

export interface LogEntry {
  timestamp: Date;
  level: LogLevel;
  message: string;
  args?: any[];
  className?: string;
  methodName?: string;
  source?: string;
}

@Injectable({
  providedIn: 'root'
})
export class LoggerService {
  private logLevel: LogLevel = environment.production ? LogLevel.WARN : LogLevel.DEBUG;
  private logs: LogEntry[] = [];
  private maxLogEntries = 1000;

  constructor() {
    this.info('Logger service initialized', { logLevel: LogLevel[this.logLevel] });
  }

  /**
   * Set the minimum log level
   */
  setLogLevel(level: LogLevel): void {
    this.logLevel = level;
    this.info('Log level changed', { newLevel: LogLevel[level] });
  }

  /**
   * Get current log level
   */
  getLogLevel(): LogLevel {
    return this.logLevel;
  }

  /**
   * Log an error message
   */
  error(message: string, ...args: any[]): void {
    this.log(LogLevel.ERROR, message, ...args);
  }

  /**
   * Log a warning message
   */
  warn(message: string, ...args: any[]): void {
    this.log(LogLevel.WARN, message, ...args);
  }

  /**
   * Log an info message
   */
  info(message: string, ...args: any[]): void {
    this.log(LogLevel.INFO, message, ...args);
  }

  /**
   * Log a debug message
   */
  debug(message: string, ...args: any[]): void {
    this.log(LogLevel.DEBUG, message, ...args);
  }

  /**
   * Log a trace message
   */
  trace(message: string, ...args: any[]): void {
    this.log(LogLevel.TRACE, message, ...args);
  }

  /**
   * Core logging method
   */
  private log(level: LogLevel, message: string, ...args: any[]): void {
    if (level > this.logLevel) {
      return; // Don't log if level is above current log level
    }

    const caller = this.getCallerInfo();
    const logEntry: LogEntry = {
      timestamp: new Date(),
      level,
      message,
      args: args.length > 0 ? args : undefined,
      className: caller.className,
      methodName: caller.methodName,
      source: caller.className && caller.methodName ? `${caller.className}.${caller.methodName}` : caller.source
    };

    // Store log entry
    this.addLogEntry(logEntry);

    // Output to console
    this.outputToConsole(logEntry);
  }

  /**
   * Extract caller information from stack trace
   */
  private getCallerInfo(): { className?: string; methodName?: string; source?: string } {
    try {
      const stack = new Error().stack;
      if (!stack) {
        return {};
      }

      const stackLines = stack.split('\n');
      // Skip the first few lines (Error, getCallerInfo, log, and the logger method)
      for (let i = 3; i < stackLines.length; i++) {
        const line = stackLines[i].trim();
        
        // Skip Angular internal calls and logger service calls
        if (line.includes('LoggerService') || 
            line.includes('angular/core') ||
            line.includes('zone.js') ||
            line.includes('rxjs/') ||
            line.includes('webpack')) {
          continue;
        }

        // Try different regex patterns for different stack trace formats
        const patterns = [
          // Pattern: at ClassName.methodName (file:line:col)
          /at (\w+)\.(\w+)\s*\(/,
          // Pattern: at ClassName.prototype.methodName (file:line:col)  
          /at (\w+)\.prototype\.(\w+)\s*\(/,
          // Pattern: at Object.methodName (file:line:col) - for services
          /at Object\.(\w+)\s*\(/,
          // Pattern: at methodName (file:line:col) - standalone functions
          /at (\w+)\s*\(/,
          // Pattern: ClassName.methodName@file:line:col (Firefox style)
          /(\w+)\.(\w+)@/,
          // Pattern: methodName@file:line:col (Firefox style)
          /(\w+)@/
        ];

        for (const pattern of patterns) {
          const match = line.match(pattern);
          if (match) {
            let className: string | undefined;
            let methodName: string;

            if (pattern.source.includes('\\.')) {
              // Patterns with class and method
              className = match[1];
              methodName = match[2];
            } else {
              // Patterns with just method or Object.method
              if (match[0].includes('Object.')) {
                className = 'Service'; // Likely an Angular service
                methodName = match[1];
              } else {
                methodName = match[1];
              }
            }
            
            // Filter out generic/internal function names
            if (
                  methodName &&
                  !['anonymous', 'Object', 'Function', '__decorate', 'callHook', 'forEach', 'map', 'filter', 'reduce'].includes(methodName)
                ) {
              // Try to extract actual class name from file path if className is generic
              if (!className || className === 'Object') {
                const fileMatch = line.match(/([^\/\\]+)\.(?:component|service|pipe|directive|guard)\.js/);
                if (fileMatch) {
                  className = fileMatch[1].charAt(0).toUpperCase() + fileMatch[1].slice(1) + 
                           (line.includes('.service.') ? 'Service' :
                            line.includes('.component.') ? 'Component' : '');
                }
              }

              return {
                className: className || 'Unknown',
                methodName,
                source: className ? `${className}.${methodName}` : methodName
              };
            }
          }
        }
      }
      
      return { source: 'Application' };
    } catch (error) {
      return { source: 'Application' };
    }
  }

  /**
   * Add log entry to internal storage
   */
  private addLogEntry(logEntry: LogEntry): void {
    this.logs.push(logEntry);
    
    // Keep only the last maxLogEntries
    if (this.logs.length > this.maxLogEntries) {
      this.logs = this.logs.slice(-this.maxLogEntries);
    }
  }

  /**
   * Output log entry to browser console
   */
  private outputToConsole(logEntry: LogEntry): void {
    const timestamp = logEntry.timestamp.toUTCString();
    const levelName = LogLevel[logEntry.level];
    const source = logEntry.source ? `[${logEntry.source}]` : '';
    const formattedMessage = `🕐 ${timestamp} | ${levelName} ${source} | ${logEntry.message}`;

    const consoleStyle = this.getConsoleStyle(logEntry.level);
    const consoleArgs = [`%c${formattedMessage}`, consoleStyle];
    
    // Add any additional arguments
    if (logEntry.args && logEntry.args.length > 0) {
      consoleArgs.push(...logEntry.args);
    }

    switch (logEntry.level) {
      case LogLevel.ERROR:
        console.error(...consoleArgs);
        break;
      case LogLevel.WARN:
        console.warn(...consoleArgs);
        break;
      case LogLevel.INFO:
        console.info(...consoleArgs);
        break;
      case LogLevel.DEBUG:
      case LogLevel.TRACE:
        console.log(...consoleArgs);
        break;
    }
  }

  /**
   * Get console styling for different log levels
   */
  private getConsoleStyle(level: LogLevel): string {
    switch (level) {
      case LogLevel.ERROR:
        return 'color: #ff4444; font-weight: bold;';
      case LogLevel.WARN:
        return 'color: #ff8800; font-weight: bold;';
      case LogLevel.INFO:
        return 'color: #0088ff; font-weight: bold;';
      case LogLevel.DEBUG:
        return 'color: #888888;';
      case LogLevel.TRACE:
        return 'color: #cccccc;';
      default:
        return '';
    }
  }

  /**
   * Get all stored log entries
   */
  getLogs(): LogEntry[] {
    return [...this.logs];
  }

  /**
   * Get logs filtered by level
   */
  getLogsByLevel(level: LogLevel): LogEntry[] {
    return this.logs.filter(log => log.level === level);
  }

  /**
   * Clear all stored logs
   */
  clearLogs(): void {
    this.logs = [];
    this.info('Log history cleared');
  }

  /**
   * Export logs as JSON string
   */
  exportLogs(): string {
    return JSON.stringify(this.logs, null, 2);
  }

  /**
   * Log HTTP requests and responses
   */
  logHttpRequest(method: string, url: string, data?: any): void {
    const caller = this.getCallerInfo();
    this.logWithCaller(LogLevel.DEBUG, `HTTP ${method} ${url}`, data, caller, 'HTTP');
  }

  /**
   * Log HTTP responses
   */
  logHttpResponse(method: string, url: string, status: number, data?: any): void {
    const message = `HTTP ${method} ${url} - ${status}`;
    const caller = this.getCallerInfo();
    const level = status >= 400 ? LogLevel.ERROR : LogLevel.DEBUG;
    this.logWithCaller(level, message, data, caller, 'HTTP');
  }

  /**
   * Log performance metrics
   */
  logPerformance(operation: string, duration: number, data?: any): void {
    const caller = this.getCallerInfo();
    this.logWithCaller(LogLevel.DEBUG, `Performance: ${operation} took ${duration}ms`, data, caller, 'PERF');
  }

  /**
   * Log with specific caller information
   */
  private logWithCaller(level: LogLevel, message: string, args: any[], caller?: any, prefix?: string): void {
    if (level > this.logLevel) {
      return;
    }

    const logEntry: LogEntry = {
      timestamp: new Date(),
      level,
      message,
      args: args.length > 0 ? args : undefined,
      className: caller?.className,
      methodName: caller?.methodName,
      source: prefix ? `${prefix}:${caller?.source || 'Unknown'}` : caller?.source
    };

    this.addLogEntry(logEntry);
    this.outputToConsole(logEntry);
  }
}